import * as Comlink from 'comlink';
import type { BackendApi } from '../../workers/backend.worker';

/*
 * BackendClient — the main-thread handle to the Backend Worker.
 *
 * Spawns the worker (a real OS thread), wraps it with Comlink, and owns the
 * session-JWT plumbing: the main thread holds the session and pushes the
 * token into the worker (which uses it in Connect call metadata), refreshing
 * it before the token's ~60s expiry. See docs/nats-frontend-architecture.md.
 *
 * LOAD PERF: create() is called in PARALLEL with the IdP client's load at boot, so
 * the worker bundle downloads + compiles + the thread spins up while the SDK does its
 * network round-trip. It inits with whatever token is available (null before
 * the SDK resolves); refreshToken() pushes the real JWT once the user is known.
 */
export class BackendClient {
  private constructor(
    readonly api: Comlink.Remote<BackendApi>,
    private readonly worker: Worker,
    private readonly getToken: () => Promise<string | null>,
    private readonly refreshTimer: number,
    private readonly onVisibilityChange: () => void,
  ) {}

  /** The current session JWT — for main-thread byte fetches that bypass the worker's
   *  Connect transport (hls.js HLS playback sends it as a Bearer via xhrSetup). */
  getAuthToken(): Promise<string | null> {
    return this.getToken();
  }

  static async create(
    getToken: () => Promise<string | null>,
    /** FORCE-fresh token (force-refresh) for the worker's on-demand recovery.
     *  Defaults to getToken, but callers should pass a skipCache variant: the
     *  reactive refresh fires precisely BECAUSE the cached token went stale, so
     *  pulling the cache again returns the same dead token and the stream re-open
     *  loops (~1.7s churn → constant value re-seeds). A skipCache mint breaks it. */
    getFreshToken: () => Promise<string | null> = getToken,
  ): Promise<BackendClient> {
    const worker = new Worker(new URL('../../workers/backend.worker.ts', import.meta.url), {
      type: 'module',
      name: 'vitesse-backend',
    });
    const api = Comlink.wrap<BackendApi>(worker);

    // Surface the worker's trace rail in the PAGE console — the worker console
    // is a separate DevTools target most debugging never looks at. Plain
    // listener alongside Comlink's; messages without Comlink protocol fields
    // are ignored by it, so the two coexist on one port.
    worker.addEventListener('message', (e: MessageEvent) => {
      const d = e.data as { __wtrace?: string; fields?: unknown } | null;
      if (d && typeof d.__wtrace === 'string') {
        if (d.fields !== undefined) console.log('[worker]', d.__wtrace, d.fields);
        else console.log('[worker]', d.__wtrace);
      }
    });

    // The proxied callback is the worker's ON-DEMAND fresh-token path: when a
    // request fails `unauthenticated` (stale token), it pulls a live
    // token through this and retries — self-healing, not just timer-based. It
    // MUST skip the SDK's token cache (getFreshToken), or the recovery returns the same
    // expired token and the stream re-open loops.
    await api.init(await getToken(), Comlink.proxy(getFreshToken));

    // Refresh the worker's token ahead of the token's ~60s expiry. NOTE:
    // intervals are throttled in hidden tabs and paused during sleep — that's
    // why the on-demand refresh above exists; this timer is the cheap warm path.
    const refreshTimer = window.setInterval(() => {
      void (async () => api.setToken(await getToken()))();
    }, 50_000);

    // Waking from a hidden tab / sleep: push a fresh token immediately instead
    // of waiting out the (possibly long-overdue) interval tick.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void (async () => api.setToken(await getToken()))();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return new BackendClient(api, worker, getToken, refreshTimer, onVisibilityChange);
  }

  /** Push the current JWT into the worker now (e.g. right after sign-in). */
  async refreshToken(): Promise<void> {
    await this.api.setToken(await this.getToken());
  }

  dispose(): void {
    window.clearInterval(this.refreshTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.worker.terminate();
  }
}
